import React, {
  useState,
  useEffect,
  useRef,
  Fragment,
  useCallback,
} from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { BsCameraVideo, BsQrCode } from "react-icons/bs";
import {
  CheckCircleIcon,
  ExclamationCircleIcon,
} from "@heroicons/react/24/solid";
import { Dialog, Transition } from "@headlessui/react";
import bookingApi from "../apis/booking.api";
import MetaData from "../component/Metadata";
import { Scanner } from "@yudiel/react-qr-scanner";
import ScanPreviewConfirmModal from "../component/ScanPreviewConfirmModal";

const PwaScanner = () => {
  const [hasPermission, setHasPermission] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showSuccessAnimation, setShowSuccessAnimation] = useState(false);
  const [additionalData, setAdditionalData] = useState(null);
  const [showError, setShowError] = useState(false);
  const [showScanner, setShowScanner] = useState(false);
  const [scannerActive, setScannerActive] = useState(false);
  const [scanInProgress, setScanInProgress] = useState(false);
  const [scannerKey, setScannerKey] = useState(0);
  const [scannerStream, setScannerStream] = useState(null);

  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewBookingId, setPreviewBookingId] = useState(null);
  const [previewBookingData, setPreviewBookingData] = useState(null);

  const scannerRef = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const resetState = useCallback(() => {
    setScannerActive(false);
    setShowScanner(false);
    setShowPopup(false);
    setShowSuccessAnimation(false);
    setShowError(false);
    setAdditionalData(null);
    setScanInProgress(false);
  }, []);

  const stopScanner = useCallback(() => {
    setScannerActive(false);
    setShowScanner(false);

    try {
      if (scannerRef.current) {
        if (typeof scannerRef.current.stopStream === "function") {
          scannerRef.current.stopStream();
        } else if (
          scannerRef.current.video &&
          scannerRef.current.video.srcObject
        ) {
          const stream = scannerRef.current.video.srcObject;
          if (stream?.getTracks) {
            stream.getTracks().forEach((t) => {
              try {
                t.stop();
              } catch (e) {}
            });
          }
          scannerRef.current.video.srcObject = null;
        }
      }

      if (scannerStream && scannerStream.getTracks) {
        scannerStream.getTracks().forEach((t) => {
          try {
            t.stop();
          } catch (e) {}
        });
      }
      setScannerStream(null);

      const videos = document.querySelectorAll("video");
      videos.forEach((v) => {
        const s = v.srcObject;
        if (s && s.getTracks) {
          s.getTracks().forEach((t) => {
            try {
              t.stop();
            } catch (e) {}
          });
        }
        try {
          v.srcObject = null;
        } catch (e) {}
      });
    } catch (err) {
      console.warn("Failed to stop camera stream:", err);
    }
  }, [scannerStream]);

  useEffect(() => {
    return () => {
      stopScanner();
    };
  }, [stopScanner]);

  const tryGetUserMedia = async () => {
    const attempts = [
      { video: { facingMode: { exact: "environment" } } },
      { video: { facingMode: { ideal: "environment" } } },
      { video: { facingMode: "user" } },
      { video: true },
    ];

    for (const constraints of attempts) {
      try {
        const stream = await navigator.mediaDevices.getUserMedia(constraints);
        return { stream, constraints };
      } catch (err) {}
    }
    throw new Error("No available camera for tested constraints.");
  };

  const attachStreamAndMount = async (stream) => {
    if (scannerStream && scannerStream.getTracks) {
      scannerStream.getTracks().forEach((t) => {
        try {
          t.stop();
        } catch (e) {}
      });
    }

    setScannerStream(stream);

    setShowScanner(false);
    setScannerActive(false);

    setTimeout(async () => {
      setScannerKey((k) => k + 1);
      setShowScanner(true);
      setScannerActive(true);

      try {
        const maybeVideo = scannerRef.current?.video ?? scannerRef.current;
        if (maybeVideo instanceof HTMLVideoElement) {
          maybeVideo.srcObject = stream;
          await maybeVideo.play().catch(() => {});
        } else if (scannerRef.current && scannerRef.current.video) {
          scannerRef.current.video.srcObject = stream;
          await scannerRef.current.video.play().catch(() => {});
        }
      } catch (attachErr) {
        console.warn("Failed to attach stream to scannerRef:", attachErr);
      }
    }, 80);
  };

  const startScanner = async () => {
    setErrorMessage("");
    try {
      if (navigator.permissions && navigator.permissions.query) {
        try {
          const permissionStatus = await navigator.permissions.query({
            name: "camera",
          });
          if (permissionStatus.state === "denied") {
            setShowPopup(true);
            setHasPermission(false);
            setErrorMessage(
              "Camera access denied. Please allow camera permissions in browser settings."
            );
            return;
          }
        } catch (permErr) {}
      }

      const { stream } = await tryGetUserMedia();
      attachStreamAndMount(stream);
      setHasPermission(true);
    } catch (err) {
      setErrorMessage(
        "Unable to start camera. Ensure HTTPS and camera is available."
      );
      setShowPopup(true);
      setHasPermission(false);
    }
  };

  const requestCameraPermission = async () => {
    setShowPopup(false);
    try {
      const { stream } = await tryGetUserMedia();
      attachStreamAndMount(stream);
      setHasPermission(true);
    } catch (err) {
      setHasPermission(false);
      setErrorMessage("Camera access denied or unavailable.");
      setShowPopup(true);
    }
  };

  const handleScan = useCallback(
    (data) => {
      if (scanInProgress) return;
      setScanInProgress(true);

      let rawValue = null;
      try {
        if (!data) {
          rawValue = null;
        } else if (Array.isArray(data)) {
          rawValue =
            data[0]?.rawValue ?? (typeof data[0] === "string" ? data[0] : null);
        } else if (typeof data === "object") {
          rawValue = data.rawValue ?? data.text ?? null;
        } else if (typeof data === "string") {
          rawValue = data;
        }
      } catch (e) {
        console.error("Error parsing scan data:", e, data);
        rawValue = null;
      }

      if (!rawValue) {
        setScanInProgress(false);
        return;
      }

      bookingApi.ScannerPreviewQr({
        payload: { qrCodeData: rawValue },
        success: (res) => {
          const payload = res?.data || {};
          const bookingData = payload?.bookingData || payload?.data || null;

          if (!bookingData) {
            setErrorMessage("No booking preview available for this QR.");
            setShowError(true);
            setScanInProgress(false);
            return;
          }

          setPreviewBookingData(bookingData);
          setPreviewModalOpen(true);
          setScanInProgress(false);

          stopScanner();
        },
        error: (err) => {
          console.error("scannerPreview error:", err);
          const msg =
            err?.response?.data?.error ||
            err?.response?.data?.message ||
            "Invalid QR or preview failed.";
          setErrorMessage(msg);
          setShowError(true);
          setScanInProgress(false);
        },
      });
    },
    [scanInProgress, stopScanner]
  );

  const handleError = (err) => {
    console.error("Scanner runtime error:", err);
    setErrorMessage(
      "Camera error. Please check permissions and device hardware."
    );
    setShowError(true);
    stopScanner();
  };

  const handleModalConfirmed = ({ bookingId, scannedNow, serverResponse }) => {
    const payload = serverResponse?.data || {};
    const bookingData = payload?.bookingData || payload?.data || null;

    if (bookingData) {
      setAdditionalData({
        message: payload?.message || "Scan accepted.",
        bookingData,
      });
    } else {
      setAdditionalData({
        message: payload?.message || "Scan accepted.",
        bookingData: { booking_id: bookingId },
      });
    }

    setShowSuccessAnimation(true);
  };

  const handleSuccessClose = () => {
    resetState();

    setTimeout(() => startScanner(), 300);
  };

  const handleErrorClose = () => {
    resetState();
    stopScanner();
  };

  const handlePopupClose = () => {
    resetState();
    stopScanner();
  };

  return (
    <>
      <MetaData title="Scanner | TicketVerz" />

      <div className="flex justify-center items-center h-full py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md mx-auto bg-white rounded-xl shadow-md overflow-hidden md:max-w-2xl">
          <div className="p-8">
            <div className="flex items-center justify-center mb-6">
              <BsQrCode className="text-4xl text-blue-600" />
              <h2 className="ml-3 mt-3 text-2xl font-bold text-gray-800">
                Ticket Scanner
              </h2>
            </div>
            <p className="text-gray-600 mb-6 text-center">
              Position the QR code within the camera frame to scan
            </p>

            <div className="mt-4 flex gap-3">
              <button
                onClick={() => {
                  const qp = new URLSearchParams(location.search).toString();
                  navigate(`/scanner/bookings?${qp}`);
                }}
                className="flex-1 px-4 py-2 border border-gray-200 rounded-md text-sm bg-white hover:shadow-sm transition"
                aria-label="Manual scan via booking list"
              >
                Manual Scan (Lookup Booking)
              </button>
            </div>

            {!scannerActive && (
              <div className="space-y-4 mt-6">
                <button
                  onClick={startScanner}
                  className="w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-[#1d275f] hover:bg-[#151d49] focus:outline-none"
                  aria-label="Start camera scanning"
                >
                  <BsCameraVideo className="mr-2" />
                  Start Camera Scanning
                </button>
              </div>
            )}

            {showScanner && scannerActive && (
              <div className="relative mt-6">
                <Scanner
                  key={scannerKey}
                  ref={scannerRef}
                  onScan={handleScan}
                  allowMultiple={false}
                  scanDelay={5000}
                  onError={handleError}
                  style={{ width: "100%", height: "auto", maxWidth: 560 }}
                  components={{
                    audio: true,
                    onOff: true,
                    torch: true,
                    zoom: true,
                    finder: true,
                  }}
                />
                <button
                  onClick={stopScanner}
                  className="mt-4 w-full flex items-center justify-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-red-600 hover:bg-red-700"
                  aria-label="Stop scanning"
                >
                  Stop Scanning
                </button>
              </div>
            )}

            <Transition appear show={showSuccessAnimation} as={Fragment}>
              <Dialog
                as="div"
                className="relative z-10"
                onClose={handleSuccessClose}
              >
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 flex items-center justify-center p-2 md:p-4 overflow-y-auto">
                  <div className="w-full max-w-sm md:max-w-md p-4 md:p-6 bg-white rounded-lg shadow-xl">
                    <div className="text-center">
                      <CheckCircleIcon className="h-12 w-12 md:h-16 md:w-16 text-green-500 mx-auto mb-3" />
                      <h3 className="text-lg md:text-xl font-semibold text-gray-900 mb-3">
                        Access Granted!
                      </h3>
                      {additionalData && (
                        <div className="text-gray-700 text-sm md:text-base mb-4 space-y-1 text-left">
                          <h3 className="text-black font-semibold text-center mb-3 md:text-lg">
                            {additionalData?.message}
                          </h3>
                          <p>
                            <span className="font-medium">
                              {additionalData?.bookingData?.type} Name:
                            </span>{" "}
                            <b>{additionalData?.bookingData?.title}</b>
                          </p>
                          <p>
                            <span className="font-medium">Name:</span>{" "}
                            <b>
                              {additionalData?.bookingData?.first_name}{" "}
                              {additionalData?.bookingData?.last_name || ""}
                            </b>
                          </p>
                          <p>
                            <span className="font-medium">Email:</span>{" "}
                            <b>{additionalData?.bookingData?.email}</b>
                          </p>
                          <p>
                            <span className="font-medium">Quantity:</span>{" "}
                            <b>{additionalData?.bookingData?.quantity}</b>
                          </p>
                          <p>
                            <span className="font-medium">Date:</span>{" "}
                            <b>
                              {
                                additionalData?.bookingData?.event_date?.split(
                                  "T"
                                )[0]
                              }
                            </b>
                          </p>
                          <p>
                            <span className="font-medium">Booking ID:</span>{" "}
                            <b>{additionalData?.bookingData?.booking_id}</b>
                          </p>

                          {additionalData?.bookingData?.type === "Movie" ? (
                            <>
                              <p className="font-medium mt-2">Seats:</p>
                              <ul className="list-disc pl-4">
                                {Object.entries(
                                  Object.entries(
                                    JSON.parse(
                                      additionalData?.bookingData?.seat_numbers
                                    )
                                  ).reduce((acc, [seat, type]) => {
                                    acc[type] = acc[type] || [];
                                    acc[type].push(seat);
                                    return acc;
                                  }, {})
                                ).map(([type, seats]) => (
                                  <li
                                    key={type}
                                    className="text-[#1d275f] font-semibold"
                                  >
                                    {type}: {seats.join(", ")}
                                  </li>
                                ))}
                              </ul>
                            </>
                          ) : (
                            <>
                              <p className="font-medium mt-2">
                                Ticket Details:
                              </p>
                              <ul className="list-disc pl-4">
                                {(additionalData?.bookingData?.tickets).map(
                                  (ticket, index) => (
                                    <li
                                      key={index}
                                      className="text-[#1d275f] font-semibold"
                                    >
                                      {ticket.ticket_name} x {ticket.quantity}
                                    </li>
                                  )
                                )}
                              </ul>
                            </>
                          )}
                        </div>
                      )}
                      <button
                        className="bg-[#1d275f] text-white px-4 py-2 md:px-8 md:py-3 rounded-lg shadow hover:bg-[#151d49] transition duration-200 mt-4 w-full"
                        onClick={handleSuccessClose}
                      >
                        Proceed
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog>
            </Transition>

            <Transition appear show={showError} as={Fragment}>
              <Dialog
                as="div"
                className="relative z-10"
                onClose={handleErrorClose}
              >
                <Transition.Child
                  as={Fragment}
                  enter="ease-out duration-300"
                  enterFrom="opacity-0"
                  enterTo="opacity-100"
                  leave="ease-in duration-200"
                  leaveFrom="opacity-100"
                  leaveTo="opacity-0"
                >
                  <div className="fixed inset-0 bg-black bg-opacity-25" />
                </Transition.Child>

                <div className="fixed inset-0 flex items-center justify-center p-2 md:p-4 overflow-y-auto">
                  <div className="w-full max-w-sm md:max-w-md p-4 md:p-6 bg-white rounded-lg shadow-xl">
                    <div className="text-center">
                      <ExclamationCircleIcon className="h-16 w-16 text-red-500 mx-auto mb-4" />
                      <h3 className="text-xl font-semibold text-red-600 mb-4">
                        Error!
                      </h3>
                      <p className="text-gray-700 mb-4">{errorMessage}</p>
                      {additionalData && (
                        <div className="text-gray-700 text-sm md:text-base mb-4 space-y-1 text-left">
                          <p>
                            <span className="font-medium">
                              {additionalData?.bookingData?.type} Name:
                            </span>{" "}
                            <b>{additionalData?.bookingData?.title}</b>
                          </p>
                          <p>
                            <span className="font-medium">Name:</span>{" "}
                            <b>
                              {additionalData?.bookingData?.first_name}{" "}
                              {additionalData?.bookingData?.last_name || ""}
                            </b>
                          </p>
                          <p>
                            <span className="font-medium">Email:</span>{" "}
                            <b>{additionalData?.bookingData?.email}</b>
                          </p>
                          <p>
                            <span className="font-medium">Quantity:</span>{" "}
                            <b>{additionalData?.bookingData?.quantity}</b>
                          </p>
                          <p>
                            <span className="font-medium">Date:</span>{" "}
                            <b>
                              {
                                additionalData?.bookingData?.event_date?.split(
                                  "T"
                                )[0]
                              }
                            </b>
                          </p>
                          <p>
                            <span className="font-medium">Booking ID:</span>{" "}
                            <b>{additionalData?.bookingData?.booking_id}</b>
                          </p>

                          {additionalData?.bookingData?.type === "Movie" ? (
                            <>
                              <p className="font-medium mt-2">Seats:</p>
                              <ul className="list-disc pl-4">
                                {Object.entries(
                                  Object.entries(
                                    JSON.parse(
                                      additionalData?.bookingData?.seat_numbers
                                    )
                                  ).reduce((acc, [seat, type]) => {
                                    acc[type] = acc[type] || [];
                                    acc[type].push(seat);
                                    return acc;
                                  }, {})
                                ).map(([type, seats]) => (
                                  <li
                                    key={type}
                                    className="text-[#1d275f] font-semibold"
                                  >
                                    {type}: {seats.join(", ")}
                                  </li>
                                ))}
                              </ul>
                            </>
                          ) : (
                            <>
                              <p className="font-medium mt-2">
                                Ticket Details:
                              </p>
                              <ul className="list-disc pl-4">
                                {(additionalData?.bookingData?.tickets).map(
                                  (ticket, index) => (
                                    <li
                                      key={index}
                                      className="text-[#1d275f] font-semibold"
                                    >
                                      {ticket.ticket_name} x {ticket.quantity}
                                    </li>
                                  )
                                )}
                              </ul>
                            </>
                          )}
                        </div>
                      )}
                      <button
                        className="bg-red-500 text-white px-8 py-3 rounded-lg shadow-xl hover:bg-red-600 transition duration-200"
                        onClick={handleErrorClose}
                      >
                        Close
                      </button>
                    </div>
                  </div>
                </div>
              </Dialog>
            </Transition>

            {/* Camera Access Popup */}
            {showPopup && (
              <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-50">
                <div className="bg-white p-6 rounded-lg shadow-xl text-center">
                  <p className="text-lg text-gray-700 mb-4">
                    To use the scanner, please allow camera access.
                  </p>
                  <button
                    className="bg-blue-500 text-white px-8 py-4 rounded-lg shadow-xl hover:bg-blue-600 transition duration-200"
                    onClick={requestCameraPermission}
                  >
                    Allow Camera Access
                  </button>
                  <button
                    className="bg-gray-300 text-gray-700 px-8 py-4 mt-4 rounded-lg shadow-xl hover:bg-gray-400 transition duration-200"
                    onClick={handlePopupClose}
                  >
                    Close
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      <ScanPreviewConfirmModal
        isOpen={previewModalOpen}
        onClose={() => {
          setPreviewModalOpen(false);
          setPreviewBookingData(null);
          setTimeout(() => startScanner(), 250);
        }}
        bookingData={previewBookingData}
        onConfirmed={({ bookingId, scannedNow, serverResponse }) =>
          handleModalConfirmed({ bookingId, scannedNow, serverResponse })
        }
        onOptimisticUpdate={async ({ bookingId, scannedNow }) => {
          return null;
        }}
      />
    </>
  );
};

export default PwaScanner;
